import { Alert, Button, Grid, Typography } from '@mui/material';
import MultiPageBuilder from 'components/MultiPageBuilder';
import FullHeightView from 'components/new/FullHeightView';
import PageLoader from 'components/PageLoader';
import Spacing from 'components/Spacing';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { Row } from 'further-ui/components/Layout';
import { numberToCurrencyString } from 'further-ui/utils';
import { sumBy } from 'lodash';
import { useFieldArray } from 'react-hook-form';
import { CapitalActionFormValues } from '../hooks/useCapitalActionForm';
import useDefaultEntryValues from '../hooks/useDefaultEntryValues';
import useGetEntryTitle from '../hooks/useGetEntryTitle';
import CapitalActionEntry from './CapitalActionEntry';

type Props = {
  onPrevious: () => void;
  onNext: () => void;
  hasUncalledEqualisations: boolean;
};

const CapitalActionEntries: React.FC<Props> = ({
  onPrevious,
  onNext,
  hasUncalledEqualisations,
}) => {
  const getEntryTitle = useGetEntryTitle();
  const { defaultEntry } = useDefaultEntryValues();

  const { vehicle } = useLayoutContext();
  const {
    formMethods: { control, watch, formState },
  } = useFormWithStateContext<CapitalActionFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries',
  });

  const capitalActionEntries = watch('entries');

  const handleAddAnotherTransaction = () => {
    append(defaultEntry);
  };

  const totalAmount = sumBy(capitalActionEntries, (entry) =>
    [
      CapitalActionEntryType.CapitalRefund,
      CapitalActionEntryType.CapitalDistribution,
    ].includes(entry.entryType)
      ? -entry.totalAmount
      : entry.totalAmount,
  );

  if (!vehicle) {
    return <PageLoader />;
  }

  const hasDistributionEntry = capitalActionEntries.some(
    (entry) => entry.entryType === CapitalActionEntryType.CapitalDistribution,
  );

  return (
    <FullHeightView
      actionBar={
        <Row centered spacing="sm" justify="flex-end">
          <Button
            variant="outlined"
            onClick={onPrevious}
            sx={{ alignSelf: 'flex-start' }}
            size="large"
          >
            Previous step
          </Button>
          <Button
            variant="contained"
            onClick={handleAddAnotherTransaction}
            disabled={hasDistributionEntry}
            size="large"
          >
            Add another transaction
          </Button>
          <Button variant="contained" onClick={onNext} size="large">
            Save and review
          </Button>
        </Row>
      }
    >
      <Grid container>
        <Grid size={{ xs: 12, lg: 11 }}>
          <Spacing size="md">
            <Typography variant="h4">
              Add capital call or distribution transaction
            </Typography>
            <MultiPageBuilder
              summaryHeader="Capital action summary"
              summaryFooterTitle="Total to be called/(distributed)"
              summaryFooterTotals={numberToCurrencyString(totalAmount, {
                currency: vehicle.baseCurrency,
                negativeInBrackets: true,
              })}
              defaultActivePageIndex={
                capitalActionEntries.length
                  ? capitalActionEntries.length - 1
                  : 0
              }
              onDeletePage={(index) => {
                remove(index);
              }}
              pageName="transaction"
            >
              {capitalActionEntries.map((entry, index) => (
                <MultiPageBuilder.Page
                  key={fields[index].id}
                  title={getEntryTitle(entry)}
                  totals={numberToCurrencyString(entry.totalAmount, {
                    currency: vehicle.baseCurrency,
                  })}
                >
                  <CapitalActionEntry
                    hasUncalledEqualisations={hasUncalledEqualisations}
                    index={index}
                    vehicleId={vehicle._id}
                    baseCurrency={vehicle.baseCurrency}
                    hasDistributionEntry={hasDistributionEntry}
                    entriesCount={capitalActionEntries.length}
                  />
                </MultiPageBuilder.Page>
              ))}
            </MultiPageBuilder>
            {!!formState.errors.entries?.message && (
              <Alert severity="error">{formState.errors.entries.message}</Alert>
            )}
          </Spacing>
        </Grid>
      </Grid>
    </FullHeightView>
  );
};

export default CapitalActionEntries;
